import React, { useEffect, useState } from "react";
import Header from "../components/Header";
import { products } from "../assets/dummy";

const ProductsPage = () => {
  // Dropdown states for each filter category
  const [isDiameterOpen, setIsDiameterOpen] = useState(true);
  const [isApplicationOpen, setIsApplicationOpen] = useState(true);
  const [isMaterialOpen, setIsMaterialOpen] = useState(true);
  const [isAccuracyOpen, setIsAccuracyOpen] = useState(true);
  const [isSensorOpen, setIsSensorOpen] = useState(true);

  // "Show All" states
  const [showAllDiameters, setShowAllDiameters] = useState(false);
  const [showAllApplications, setShowAllApplications] = useState(false);
  const [showAllMaterials, setShowAllMaterials] = useState(false);
  const [showAllAccuracies, setShowAllAccuracies] = useState(false);
  const [showAllSensors, setShowAllSensors] = useState(false);

  // Filter states
  const [selectedDiameters, setSelectedDiameters] = useState<string[]>([]);
  const [selectedApplications, setSelectedApplications] = useState<string[]>(
    []
  );
  const [selectedMaterials, setSelectedMaterials] = useState<string[]>([]);
  const [selectedAccuracies, setSelectedAccuracies] = useState<string[]>([]);
  const [selectedSensors, setSelectedSensors] = useState<string[]>([]);

  const [filteredProducts, setFilteredProducts] = useState(products);
  const [isLoading, setIsLoading] = useState(false);

  // Update filtered products whenever any filter changes
  useEffect(() => {
    const filtered = products.filter((product) => {
      const productDiameters = product.diameter || [];
      const productApplications = product.usage ? [product.usage] : [];
      const productMaterials = product.material
        ? Array.isArray(product.material)
          ? product.material
          : [product.material]
        : [];
      const productAccuracies = product.accuracy ? [product.accuracy] : [];
      const productSensors = product.sensor ? [product.sensor] : [];

      const matchDiameter =
        selectedDiameters.length === 0 ||
        productDiameters.some((d) => selectedDiameters.includes(d));
      const matchApplication =
        selectedApplications.length === 0 ||
        productApplications.some((a) => selectedApplications.includes(a));
      const matchMaterial =
        selectedMaterials.length === 0 ||
        productMaterials.some((m) => selectedMaterials.includes(m));
      const matchAccuracy =
        selectedAccuracies.length === 0 ||
        productAccuracies.some((a) => selectedAccuracies.includes(a));
      const matchSensor =
        selectedSensors.length === 0 ||
        productSensors.some((s) => selectedSensors.includes(s));

      return (
        matchDiameter &&
        matchApplication &&
        matchMaterial &&
        matchAccuracy &&
        matchSensor
      );
    });
    setFilteredProducts(filtered);
  }, [
    selectedDiameters,
    selectedApplications,
    selectedMaterials,
    selectedAccuracies,
    selectedSensors,
  ]);

  // Generic filter change handler
  const handleFilterChange = (filterType: string, value: string) => {
    setIsLoading(true);
    switch (filterType) {
      case "diameter":
        setSelectedDiameters((prev) =>
          prev.includes(value)
            ? prev.filter((v) => v !== value)
            : [...prev, value]
        );
        break;
      case "application":
        setSelectedApplications((prev) =>
          prev.includes(value)
            ? prev.filter((v) => v !== value)
            : [...prev, value]
        );
        break;
      case "material":
        setSelectedMaterials((prev) =>
          prev.includes(value)
            ? prev.filter((v) => v !== value)
            : [...prev, value]
        );
        break;
      case "accuracy":
        setSelectedAccuracies((prev) =>
          prev.includes(value)
            ? prev.filter((v) => v !== value)
            : [...prev, value]
        );
        break;
      case "sensor":
        setSelectedSensors((prev) =>
          prev.includes(value)
            ? prev.filter((v) => v !== value)
            : [...prev, value]
        );
        break;
      default:
        break;
    }
    setTimeout(() => setIsLoading(false), 500);
  };

  // Helper functions to get unique values and filter out undefined explicitly
  const uniqueDiameters = [
    ...new Set(products.flatMap((product) => product.diameter || [])),
  ];
  const uniqueApplications = [
    ...new Set(
      products
        .map((product) => product.usage)
        .filter((usage): usage is string => usage !== undefined)
    ),
  ];
  const uniqueMaterials = [
    ...new Set(
      products.flatMap((product) =>
        product.material
          ? Array.isArray(product.material)
            ? product.material
            : [product.material]
          : []
      )
    ),
  ];
  const uniqueAccuracies = [
    ...new Set(
      products
        .map((product) => product.accuracy)
        .filter((acc): acc is string => acc !== undefined)
    ),
  ];
  const uniqueSensors = [
    ...new Set(
      products
        .map((product) => product.sensor)
        .filter((sensor): sensor is string => sensor !== undefined)
    ),
  ];

  return (
    <div className="bg-white">
      <div className="flex min-h-screen flex-col">
        <Header />
        <div className="relative">
          {/* Hero Section */}
          <section
            className="relative flex min-h-[400px] flex-col justify-end bg-cover bg-center pb-16 md:min-h-[456px] md:pb-14"
            style={{
              backgroundImage: `url(${require("../assets/images/background2.jpg")})`,
            }}
          >
            <div className="absolute inset-0 bg-black opacity-50"></div>
            <div className="container relative z-10 flex w-full flex-col justify-end text-white">
              <h1 className="font-oswald text-3.5xl font-semibold uppercase text-white md:text-6xl">
                Water Meter
              </h1>
              <div className="mt-4 md:mt-8 md:flex">
                <div className="mb-2.5 last:mb-0 md:mb-0 md:mr-8">
                  <a
                    tabIndex={0}
                    href="cat.pdf"
                    target="_blank"
                    className="ignore-has-link width-large inline-block focus:outline-none"
                    rel="noopener"
                  >
                    <div
                      tabIndex={-1}
                      className="content font-oswald md:leading-6.5 flex w-full items-center justify-center rounded-md border border-white bg-transparent px-4 py-2 text-lg uppercase leading-6 text-white transition-all duration-200 hover:bg-white hover:text-blue-800 active:border-gray-200 md:w-auto md:py-3"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="icon sprite-globals mr-2 h-6 w-6"
                      >
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="17 10 12 15 7 10" />
                        <line x1="12" y1="15" x2="12" y2="3" />
                      </svg>

                      <div className="text-left font-medium">
                        <p>Product catalog</p>
                      </div>
                    </div>
                  </a>
                </div>
              </div>
            </div>
          </section>

          <div className="overflow-x-hidden">
            <div className="container px-8">
              <div className="relative z-40 mt-16 flex items-center justify-between bg-white py-4">
                <div
                  onClick={() => {}}
                  tabIndex={0}
                  className="flex cursor-pointer select-none items-center text-gray-600"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="icon sprite-globals inline-block h-8 w-8 text-gray-500 md:h-6 md:w-6"
                  >
                    <path d="M3 6h18" />
                    <path d="M9 12h6" />
                    <path d="M5 18h14" />
                  </svg>
                  <span className="ml-2 hidden font-semibold md:block">
                    Filters
                  </span>
                </div>
                <div className="flex items-center">
                  <div className="mr-2 md:relative md:mr-8">
                    <div
                      className="absolute right-0 top-[34px] z-50 w-[84%] rounded-lg bg-white text-gray-600"
                      style={{ display: "none" }}
                    >
                      <div className="p-2 text-sm font-semibold"></div>
                      <div className="p-2">No results</div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="full-width mb-4 h-px bg-gray-400"></div>
              <main className="relative mb-16 flex min-h-[376px]">
                {/* Sidebar with filters */}
                <div className="mr-6 shrink-0 overflow-hidden transition-all duration-500 w-[288px]">
                  <div className="w-[288px]">
                    <div className="w-full">
                      {/* Nominal Diameter Filter */}
                      <div tabIndex={0} className="mb-4">
                        <div
                          onClick={() => setIsDiameterOpen(!isDiameterOpen)}
                          className="flex min-h-[40px] cursor-pointer justify-between p-2 text-gray-800 transition hover:bg-gray-400"
                        >
                          <div className="font-poppins select-none font-semibold">
                            Nominal Diameter
                          </div>
                          {isDiameterOpen ? (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="w-4 h-4"
                            >
                              <polyline points="18 15 12 9 6 15" />
                            </svg>
                          ) : (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="w-4 h-4"
                            >
                              <polyline points="6 9 12 15 18 9" />
                            </svg>
                          )}
                        </div>
                        {isDiameterOpen && (
                          <div className="space-y-1 bg-white">
                            {uniqueDiameters
                              .slice(0, showAllDiameters ? undefined : 5)
                              .map((item, index) => (
                                <div key={index} className="p-2 text-gray-600">
                                  <label
                                    htmlFor={`diameter-filter-${item}`}
                                    className="checkbox has-link relative flex cursor-pointer items-center pb-1"
                                  >
                                    <input
                                      id={`diameter-filter-${item}`}
                                      name={`diameter-filter-${item}`}
                                      type="checkbox"
                                      className="invisible absolute left-0 top-0 h-0 w-0"
                                      value={item}
                                      checked={selectedDiameters.includes(item)}
                                      onChange={() =>
                                        handleFilterChange("diameter", item)
                                      }
                                    />
                                    <div className="relative left-0 top-1 h-5 w-5">
                                      <div className="absolute inset-0 flex items-center justify-center">
                                        {selectedDiameters.includes(item) && (
                                          <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            className="m-auto block text-white"
                                            width="11"
                                            height="8"
                                          >
                                            <path d="M20 6l-10 12L4 10" />
                                          </svg>
                                        )}
                                      </div>
                                      <div
                                        tabIndex={0}
                                        className={`h-5 w-5 rounded border outline-none focus:border-2 ${
                                          selectedDiameters.includes(item)
                                            ? "border-blue-800 bg-blue-800"
                                            : "group-checkbox-hover border-gray-300"
                                        }`}
                                      ></div>
                                    </div>
                                    <div className="relative top-1 ml-2 flex-1 select-none text-base text-gray-400">
                                      {item}
                                    </div>
                                  </label>
                                </div>
                              ))}
                            {uniqueDiameters.length > 5 && (
                              <button
                                type="button"
                                onClick={() =>
                                  setShowAllDiameters(!showAllDiameters)
                                }
                                className="cursor-pointer p-2 text-blue-800"
                              >
                                {showAllDiameters ? "Show Less" : "Show All"}
                              </button>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Application Filter */}
                      <div tabIndex={0} className="mb-4">
                        <div
                          onClick={() =>
                            setIsApplicationOpen(!isApplicationOpen)
                          }
                          className="flex min-h-[40px] cursor-pointer justify-between p-2 text-gray-800 transition hover:bg-gray-400"
                        >
                          <div className="font-poppins select-none font-semibold">
                            Application
                          </div>
                          {isApplicationOpen ? (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="w-4 h-4"
                            >
                              <polyline points="18 15 12 9 6 15" />
                            </svg>
                          ) : (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="w-4 h-4"
                            >
                              <polyline points="6 9 12 15 18 9" />
                            </svg>
                          )}
                        </div>
                        {isApplicationOpen && (
                          <div className="space-y-1 bg-white">
                            {uniqueApplications
                              .slice(0, showAllApplications ? undefined : 5)
                              .map((item, index) => (
                                <div key={index} className="p-2 text-gray-600">
                                  <label
                                    htmlFor={`application-filter-${item}`}
                                    className="checkbox has-link relative flex cursor-pointer items-center pb-1"
                                  >
                                    <input
                                      id={`application-filter-${item}`}
                                      name={`application-filter-${item}`}
                                      type="checkbox"
                                      className="invisible absolute left-0 top-0 h-0 w-0"
                                      value={item}
                                      checked={selectedApplications.includes(
                                        item
                                      )}
                                      onChange={() =>
                                        handleFilterChange("application", item)
                                      }
                                    />
                                    <div className="relative left-0 top-1 h-5 w-5">
                                      <div className="absolute inset-0 flex items-center justify-center">
                                        {selectedApplications.includes(
                                          item
                                        ) && (
                                          <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            className="m-auto block text-white"
                                            width="11"
                                            height="8"
                                          >
                                            <path d="M20 6l-10 12L4 10" />
                                          </svg>
                                        )}
                                      </div>
                                      <div
                                        tabIndex={0}
                                        className={`h-5 w-5 rounded border outline-none focus:border-2 ${
                                          selectedApplications.includes(item)
                                            ? "border-blue-800 bg-blue-800"
                                            : "group-checkbox-hover border-gray-300"
                                        }`}
                                      ></div>
                                    </div>
                                    <div className="relative top-1 ml-2 flex-1 select-none text-base text-gray-400">
                                      {item}
                                    </div>
                                  </label>
                                </div>
                              ))}
                            {uniqueApplications.length > 5 && (
                              <button
                                type="button"
                                onClick={() =>
                                  setShowAllApplications(!showAllApplications)
                                }
                                className="cursor-pointer p-2 text-blue-800"
                              >
                                {showAllApplications ? "Show Less" : "Show All"}
                              </button>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Material Filter */}
                      <div tabIndex={0} className="mb-4">
                        <div
                          onClick={() => setIsMaterialOpen(!isMaterialOpen)}
                          className="flex min-h-[40px] cursor-pointer justify-between p-2 text-gray-800 transition hover:bg-gray-400"
                        >
                          <div className="font-poppins select-none font-semibold">
                            Material
                          </div>
                          {isMaterialOpen ? (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="w-4 h-4"
                            >
                              <polyline points="18 15 12 9 6 15" />
                            </svg>
                          ) : (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="w-4 h-4"
                            >
                              <polyline points="6 9 12 15 18 9" />
                            </svg>
                          )}
                        </div>
                        {isMaterialOpen && (
                          <div className="space-y-1 bg-white">
                            {uniqueMaterials
                              .slice(0, showAllMaterials ? undefined : 5)
                              .map((item, index) => (
                                <div key={index} className="p-2 text-gray-600">
                                  <label
                                    htmlFor={`material-filter-${item}`}
                                    className="checkbox has-link relative flex cursor-pointer items-center pb-1"
                                  >
                                    <input
                                      id={`material-filter-${item}`}
                                      name={`material-filter-${item}`}
                                      type="checkbox"
                                      className="invisible absolute left-0 top-0 h-0 w-0"
                                      value={item}
                                      checked={selectedMaterials.includes(item)}
                                      onChange={() =>
                                        handleFilterChange("material", item)
                                      }
                                    />
                                    <div className="relative left-0 top-1 h-5 w-5">
                                      <div className="absolute inset-0 flex items-center justify-center">
                                        {selectedMaterials.includes(item) && (
                                          <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            className="m-auto block text-white"
                                            width="11"
                                            height="8"
                                          >
                                            <path d="M20 6l-10 12L4 10" />
                                          </svg>
                                        )}
                                      </div>
                                      <div
                                        tabIndex={0}
                                        className={`h-5 w-5 rounded border outline-none focus:border-2 ${
                                          selectedMaterials.includes(item)
                                            ? "border-blue-800 bg-blue-800"
                                            : "group-checkbox-hover border-gray-300"
                                        }`}
                                      ></div>
                                    </div>
                                    <div className="relative top-1 ml-2 flex-1 select-none text-base text-gray-400">
                                      {item}
                                    </div>
                                  </label>
                                </div>
                              ))}
                            {uniqueMaterials.length > 5 && (
                              <button
                                type="button"
                                onClick={() =>
                                  setShowAllMaterials(!showAllMaterials)
                                }
                                className="cursor-pointer p-2 text-blue-800"
                              >
                                {showAllMaterials ? "Show Less" : "Show All"}
                              </button>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Accuracy & Standard Filter */}
                      <div tabIndex={0} className="mb-4">
                        <div
                          onClick={() => setIsAccuracyOpen(!isAccuracyOpen)}
                          className="flex min-h-[40px] cursor-pointer justify-between p-2 text-gray-800 transition hover:bg-gray-400"
                        >
                          <div className="font-poppins select-none font-semibold">
                            Accuracy & Standard
                          </div>
                          {isAccuracyOpen ? (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="w-4 h-4"
                            >
                              <polyline points="18 15 12 9 6 15" />
                            </svg>
                          ) : (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="w-4 h-4"
                            >
                              <polyline points="6 9 12 15 18 9" />
                            </svg>
                          )}
                        </div>
                        {isAccuracyOpen && (
                          <div className="space-y-1 bg-white">
                            {uniqueAccuracies
                              .slice(0, showAllAccuracies ? undefined : 5)
                              .map((item, index) => (
                                <div key={index} className="p-2 text-gray-600">
                                  <label
                                    htmlFor={`accuracy-filter-${item}`}
                                    className="checkbox has-link relative flex cursor-pointer items-center pb-1"
                                  >
                                    <input
                                      id={`accuracy-filter-${item}`}
                                      name={`accuracy-filter-${item}`}
                                      type="checkbox"
                                      className="invisible absolute left-0 top-0 h-0 w-0"
                                      value={item}
                                      checked={selectedAccuracies.includes(
                                        item
                                      )}
                                      onChange={() =>
                                        handleFilterChange("accuracy", item)
                                      }
                                    />
                                    <div className="relative left-0 top-1 h-5 w-5">
                                      <div className="absolute inset-0 flex items-center justify-center">
                                        {selectedAccuracies.includes(item) && (
                                          <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            className="m-auto block text-white"
                                            width="11"
                                            height="8"
                                          >
                                            <path d="M20 6l-10 12L4 10" />
                                          </svg>
                                        )}
                                      </div>
                                      <div
                                        tabIndex={0}
                                        className={`h-5 w-5 rounded border outline-none focus:border-2 ${
                                          selectedAccuracies.includes(item)
                                            ? "border-blue-800 bg-blue-800"
                                            : "group-checkbox-hover border-gray-300"
                                        }`}
                                      ></div>
                                    </div>
                                    <div className="relative top-1 ml-2 flex-1 select-none text-base text-gray-400">
                                      {item}
                                    </div>
                                  </label>
                                </div>
                              ))}
                            {uniqueAccuracies.length > 5 && (
                              <button
                                type="button"
                                onClick={() =>
                                  setShowAllAccuracies(!showAllAccuracies)
                                }
                                className="cursor-pointer p-2 text-blue-800"
                              >
                                {showAllAccuracies ? "Show Less" : "Show All"}
                              </button>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Sensor Type Filter */}
                      <div tabIndex={0} className="mb-4">
                        <div
                          onClick={() => setIsSensorOpen(!isSensorOpen)}
                          className="flex min-h-[40px] cursor-pointer justify-between p-2 text-gray-800 transition hover:bg-gray-400"
                        >
                          <div className="font-poppins select-none font-semibold">
                            Sensor Type
                          </div>
                          {isSensorOpen ? (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="w-4 h-4"
                            >
                              <polyline points="18 15 12 9 6 15" />
                            </svg>
                          ) : (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="w-4 h-4"
                            >
                              <polyline points="6 9 12 15 18 9" />
                            </svg>
                          )}
                        </div>
                        {isSensorOpen && (
                          <div className="space-y-1 bg-white">
                            {uniqueSensors
                              .slice(0, showAllSensors ? undefined : 5)
                              .map((item, index) => (
                                <div key={index} className="p-2 text-gray-600">
                                  <label
                                    htmlFor={`sensor-filter-${item}`}
                                    className="checkbox has-link relative flex cursor-pointer items-center pb-1"
                                  >
                                    <input
                                      id={`sensor-filter-${item}`}
                                      name={`sensor-filter-${item}`}
                                      type="checkbox"
                                      className="invisible absolute left-0 top-0 h-0 w-0"
                                      value={item}
                                      checked={selectedSensors.includes(item)}
                                      onChange={() =>
                                        handleFilterChange("sensor", item)
                                      }
                                    />
                                    <div className="relative left-0 top-1 h-5 w-5">
                                      <div className="absolute inset-0 flex items-center justify-center">
                                        {selectedSensors.includes(item) && (
                                          <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            className="m-auto block text-white"
                                            width="11"
                                            height="8"
                                          >
                                            <path d="M20 6l-10 12L4 10" />
                                          </svg>
                                        )}
                                      </div>
                                      <div
                                        tabIndex={0}
                                        className={`h-5 w-5 rounded border outline-none focus:border-2 ${
                                          selectedSensors.includes(item)
                                            ? "border-blue-800 bg-blue-800"
                                            : "group-checkbox-hover border-gray-300"
                                        }`}
                                      ></div>
                                    </div>
                                    <div className="relative top-1 ml-2 flex-1 select-none text-base text-gray-400">
                                      {item}
                                    </div>
                                  </label>
                                </div>
                              ))}
                            {uniqueSensors.length > 5 && (
                              <button
                                type="button"
                                onClick={() =>
                                  setShowAllSensors(!showAllSensors)
                                }
                                className="cursor-pointer p-2 text-blue-800"
                              >
                                {showAllSensors ? "Show Less" : "Show All"}
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Product List */}
                <div className="flex-1">
                  {isLoading ? (
                    <div className="flex items-center justify-center">
                      <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-t-2 border-blue-800"></div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
                      {filteredProducts.map((product) => (
                        <div
                          key={product.id}
                          className="inline-block h-full w-full rounded-lg bg-gray-100 p-4 lg:flex lg:flex-col"
                        >
                          <a
                            href={product.link}
                            className="h-full w-full lg:flex lg:flex-1 lg:flex-col"
                          >
                            <div className="flex-1 lg:flex lg:flex-col">
                              <div className="pb-2/3 relative mb-2 flex text-center">
                                <img
                                  src={product.image}
                                  alt={product.title}
                                  loading="lazy"
                                  width="840"
                                  height="auto"
                                  className="inset-0 h-full w-full object-contain"
                                />
                              </div>
                              <div className="container my-auto flex items-center justify-center">
                                <div className="font-oswald md:text-3.5xl mr-1.5 w-1/3 max-w-[100px] text-wrap text-2xl font-semibold uppercase text-blue-800 md:mr-6 md:max-w-[160px]">
                                  {product.title}
                                </div>
                                <div className="md:mad-w-[240px] list w-2/3 max-w-[161px] border-l border-blue-900 pl-1.5 md:pl-6">
                                  {product.descriptions.map(
                                    (feature, index) => (
                                      <p key={index} className="font-poppins">
                                        {feature}
                                      </p>
                                    )
                                  )}
                                </div>
                              </div>
                            </div>
                          </a>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </main>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductsPage;
